import { useHandler } from "@redotech/react-util/hook";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { isRedoEmailAddress } from "@redotech/redo-model/redo-domains";
import { memo, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SettingsMovingModal } from "./onboarding-modals/settings-moving-modal";

const LOCAL_STORAGE_KEY = "redo.settings-moving-dismissed";

function readPersistedDismissal(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writePersistedDismissal() {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
  } catch {
    // localStorage unavailable (private mode, quota exhausted); fall back to
    // in-memory dismissal for this tab only.
  }
}

export const SettingsMovingModalGate = memo(function SettingsMovingModalGate() {
  const { pathname } = useLocation();
  const user = useContext(UserContext);

  const inSettings = pathname.includes("/settings");
  const userIsRedo = isRedoEmailAddress(user?.email);

  const [permanentlyDismissed, setPermanentlyDismissed] = useState(() =>
    readPersistedDismissal(),
  );

  const [closedThisVisit, setClosedThisVisit] = useState(false);
  useEffect(() => {
    if (!inSettings) {
      setClosedThisVisit(false);
    }
  }, [inSettings]);

  const [delayElapsed, setDelayElapsed] = useState(false);
  useEffect(() => {
    if (!inSettings) {
      setDelayElapsed(false);
      return;
    }
    const timer = setTimeout(() => setDelayElapsed(true), 600);
    return () => clearTimeout(timer);
  }, [inSettings]);

  const handleClose = useHandler((dontShowAgain: boolean) => {
    setClosedThisVisit(true);
    if (dontShowAgain) {
      setPermanentlyDismissed(true);
      writePersistedDismissal();
    }
  });

  if (userIsRedo) {
    return null;
  }
  if (!inSettings) {
    return null;
  }
  if (permanentlyDismissed) {
    return null;
  }
  if (closedThisVisit) {
    return null;
  }
  if (!delayElapsed) {
    return null;
  }

  return <SettingsMovingModal onClose={handleClose} />;
});
